import { RtcBotType } from 'app/constants/enums/RtcBotType';
/* global FormData */
import { sessionRequest } from 'app/models/auth/actions';
import {
  createBotBooking,
  createMeetingBooking,
  fetchBotBooking,
  fetchCombinedLessonBooking,
  fetchMeetingBooking,
} from 'app/models/entities/remote';
import { BookingType } from 'app/models/rtc/booking/constants';
import { getAllParticipants } from 'app/models/rtc/selectors';
import { updateFromServer } from 'app/remote';
import { getResponseId } from 'app/remote/response';
import { debug } from 'app/services/logger';
import { BotLaunchMeta } from 'app/services/rtc/BotState';
import { EntityStringId } from 'app/types/entity';
import { postChatImage, postLaunchBot } from 'app/utils/api';
import { CancelToken } from 'app/utils/cancelToken';
import { decodeWithDefault, decodeOrThrow } from 'app/utils/ioTs';
import { AppDispatch, AppGetState } from 'redux';
import { IdentityEntityStringIdCodec } from 'app/types/entity/codecs';
import { IPresenceAction } from '@engoo/realtime-protocol/presence';
import { EntityCreator, EntityFetcher } from 'app/models/entities/remote/next';

type BookingCreators = {
  [key: string]: EntityCreator | undefined;
};

type BookingFetchers = {
  [key in BookingType]: EntityFetcher;
};

const bookingCreators: BookingCreators = {
  [BookingType.Meeting]:              createMeetingBooking,
  [BookingType.TeacherRegistration]:  createBotBooking,
  [BookingType.TestCall]:             createBotBooking,
};

const bookingFetchers: BookingFetchers = {
  [BookingType.Meeting]:              fetchMeetingBooking,
  [BookingType.Lesson]:               fetchCombinedLessonBooking,
  [BookingType.TeacherRegistration]:  fetchBotBooking,
  [BookingType.TestCall]:             fetchBotBooking,
};

export const createBooking = (
  type: BookingType,
  cancelToken: CancelToken,
) => {
  return async (dispatch: AppDispatch) => {
    const creator = bookingCreators[type];

    if (typeof creator === 'undefined') {
      throw new Error(`${type} cannot be instantiated.`);
    }

    return await dispatch(creator({}, cancelToken));
  };
};

export const fetchBooking = (
  type: BookingType,
  id: EntityStringId,
  cancelToken: CancelToken,
) => {
  return async (dispatch: AppDispatch, getState: AppGetState): Promise<EntityStringId> => {
    const fetcher = bookingFetchers[type];
    const response = await dispatch(fetcher(id, cancelToken));
    return getResponseId(response);
  };
};

export const fetchParticipants = (
  type: BookingType,
  id: EntityStringId,
) => {
  return (dispatch: AppDispatch) => {
    dispatch(bookingFetchers[type](id));
  };
};

export const launchBot = (
  roomId: EntityStringId,
  botType: RtcBotType,
) => {
  return async (dispatch: AppDispatch) => {
    debug('Launching bot.', { roomId, botType });

    const postAction = sessionRequest(postLaunchBot(roomId, botType));
    const result = await dispatch(postAction);

    dispatch(updateFromServer(result));

    const participantId = getResponseId(result);

    // Log the participant ID and any meta data (like the launchId) from the response.
    debug('Launched the bot.', { participantId, meta: result.meta });

    return { participantId, meta: decodeWithDefault(BotLaunchMeta, result.meta, {}) };
  };
};

type UploadChatImageOptions = {
  [key: string]: Blob | string;
};

export const uploadChatImage = (
  roomId: EntityStringId,
  options: UploadChatImageOptions,
) => {
  return async (dispatch: AppDispatch) => {
    // Since the image file is attached, the data will be sent as a multipart form
    // rather than JSON
    const formData = new FormData();
    Object.keys(options).forEach((key) => {
      formData.append(key, options[key]);
    });

    const result = await dispatch(sessionRequest(postChatImage(roomId, formData)));
    const { data: { url, id } } = result;
    return { url, id };
  };
};

/**
 * Update booking information if an unknown or renamed participant is detected.
 */
export const discoverUnknownParticipants = (
  actions: IPresenceAction[],
  type: BookingType,
  bookingId: EntityStringId,
) => {
  return async (dispatch: AppDispatch, getState: AppGetState) => {
    const knownParticipants = getAllParticipants(getState());

    const participantIsUnknown = (action: IPresenceAction) => {
      const peerId = decodeOrThrow(IdentityEntityStringIdCodec, action.peerId);

      return (
        typeof knownParticipants === 'undefined' || !knownParticipants.has(peerId)
      );
    };

    const participantUnknownOrRenamed = actions.some((action) => participantIsUnknown(action));

    if (participantUnknownOrRenamed) {
      // Generally the unknown participant case should not occur.
      // Scenario:
      //   - User A fetches the room, gets a token (only backend, no realtime)
      //   - User B fetches the room, gets a token, joins, leaves
      //   - User A joins
      // Then:
      //   User A will have no idea about User B, since it's not in peers (not in the room),
      //   and it's not in the fetched room state.
      // Current Resolution:
      //   Refresh the booking data from the server.
      dispatch(fetchParticipants(type, bookingId));
    }
  };
};
